import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { NotificationService } from '../../../../../../common/services';
import { alphanumericSortByName } from '../../../../../../common/utils';
import { DockBrief } from '../../../../administration/dock/models';
import { CompartmentRuleItem } from '../../../../administration/compartment/models';
import {
  AllocationDetailsModel,
  AllocationResponseModel,
  AllocationUliDocksResponse,
  AllocationUliRuleSlot,
  AllocationUliRulesResponse,
  AllocationViewTimeslotInputModel,
  AllocationViewTimeslotOutputModel,
} from '../../models';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AllocationViewActionBaseState } from '../base';
import { AllocationViewActionsCompartmentsService } from '../services';
import {
  AllocationViewAllocateActionAllocateByRuleUli,
  AllocationViewAllocateActionAutoAllocateUli,
  AllocationViewAllocateActionLoadAllocationDetails,
  AllocationViewAllocateActionLoadCompartments,
  AllocationViewAllocateActionLoadDocks,
  AllocationViewAllocateActionLoadProposedTimeslot,
  AllocationViewAllocateActionLoadRuleSlots,
  AllocationViewAllocateActionLoadRuleSlotsWithoutDetails,
  AllocationViewAllocateActionLoadRules,
  AllocationViewAllocateActionManualAllocateUli,
  AllocationViewAllocateActionResetProposedTimeslot,
  AllocationViewAllocateActionSetCloseAfterCompleteState,
  AllocationViewAllocateActionSetInitialState,
  AllocationViewAllocateActionUpdateAllocationDetails,
} from './allocation-view-allocate-action.actions';
import { AllocationViewAllocateActionService } from './allocation-view-allocate-action.service';
import { ActiveCompartmentsModel } from '../../uli';

export interface AllocationViewAllocateActionStateModel {
  allocationDetails: AllocationDetailsModel;
  allocationDockOptionList: DockBrief[];
  allocationRuleOptionList: CompartmentRuleItem[];
  allocationRuleSlotOptionList: AllocationUliRuleSlot[];
  proposedTimeslot: AllocationViewTimeslotInputModel;
  compartments: ActiveCompartmentsModel[];
  closeContainerAfterComplete: boolean;
  responseData: AllocationResponseModel;
  loaded: boolean;
}

const initialState: AllocationViewAllocateActionStateModel = {
  allocationDetails: null,
  allocationDockOptionList: [],
  allocationRuleOptionList: null,
  allocationRuleSlotOptionList: null,
  proposedTimeslot: null,
  compartments: [],
  closeContainerAfterComplete: true,
  responseData: null,
  loaded: false,
};

@State({
  name: 'allocationViewActionsAllocate',
  defaults: initialState,
})
@Injectable()
export class AllocationViewAllocateActionState extends AllocationViewActionBaseState {
  @Selector()
  static getDocksForAllocation(state: AllocationViewAllocateActionStateModel): DockBrief[] {
    return state.allocationDockOptionList;
  }

  @Selector()
  static getRulesForAllocation(state: AllocationViewAllocateActionStateModel): CompartmentRuleItem[] {
    return state.allocationRuleOptionList;
  }

  @Selector()
  static getRuleSlotsForAllocation(state: AllocationViewAllocateActionStateModel): AllocationUliRuleSlot[] {
    return state.allocationRuleSlotOptionList;
  }

  @Selector()
  static getProposedTimeslot(state: AllocationViewAllocateActionStateModel): AllocationViewTimeslotInputModel {
    return state.proposedTimeslot;
  }

  @Selector()
  static getAllocationDetails(state: AllocationViewAllocateActionStateModel): AllocationDetailsModel {
    return state.allocationDetails;
  }

  @Selector()
  static getCompartments(state: AllocationViewAllocateActionStateModel): ActiveCompartmentsModel[] {
    return state.compartments;
  }

  @Selector()
  static getResponseData(state: AllocationViewAllocateActionStateModel): AllocationResponseModel {
    return state.responseData;
  }

  @Selector()
  static getLoaded(state: AllocationViewAllocateActionStateModel): boolean {
    return state.loaded;
  }

  constructor(
    private allocationViewAllocateActionService: AllocationViewAllocateActionService,
    private compartmentsService: AllocationViewActionsCompartmentsService,
    notificationService: NotificationService,
    store: Store,
  ) {
    super(notificationService, store);
  }

  @Action(AllocationViewAllocateActionLoadAllocationDetails)
  getAllocationDetails(
    ctx: StateContext<AllocationViewAllocateActionStateModel>,
    { payload: { identifier } }: AllocationViewAllocateActionLoadAllocationDetails,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewAllocateActionService.getAllocationDetails(identifier).pipe(
      map((allocationDetails: AllocationDetailsModel) => {
        ctx.patchState({ allocationDetails, loaded: true });
      }),
      catchError(() => {
        ctx.patchState({ loaded: true });
        return of(this.notificationService.error('common.validation.error.noAllocationDetails'));
      }),
    );
  }

  @Action(AllocationViewAllocateActionUpdateAllocationDetails)
  updateAllocationDetails(
    ctx: StateContext<AllocationViewAllocateActionStateModel>,
    { payload }: AllocationViewAllocateActionUpdateAllocationDetails,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ loaded: false });
    return this.allocationViewAllocateActionService.updateAllocationDetails(payload).pipe(
      map((allocationDetails: AllocationDetailsModel) => {
        this.finishAction('api.uli.success.detailsUpdate', false);
        ctx.patchState({ allocationDetails, loaded: true });
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewAllocateActionLoadCompartments)
  getCompartments(
    ctx: StateContext<AllocationViewAllocateActionStateModel>,
    { payload }: AllocationViewAllocateActionLoadCompartments,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.compartmentsService.getCompartments(payload).pipe(
      map((items: ActiveCompartmentsModel[]) => {
        ctx.patchState({
          compartments: [...items],
        });
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewAllocateActionLoadDocks)
  getAllocationDocks(
    ctx: StateContext<AllocationViewAllocateActionStateModel>,
    { payload }: AllocationViewAllocateActionLoadDocks,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewAllocateActionService.getAllocationDocks(payload).pipe(
      map(({ docks }: AllocationUliDocksResponse) => {
        ctx.patchState({
          allocationDockOptionList: alphanumericSortByName(docks),
          loaded: true,
        });
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewAllocateActionLoadRules)
  getAllocationRules(
    ctx: StateContext<AllocationViewAllocateActionStateModel>,
    { payload }: AllocationViewAllocateActionLoadRules,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewAllocateActionService.getAllocationRules(payload).pipe(
      map(({ rules }: AllocationUliRulesResponse) => {
        ctx.patchState({
          allocationRuleOptionList: rules,
          loaded: true,
        });
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewAllocateActionLoadRuleSlots)
  getAllocationRuleSlots(
    ctx: StateContext<AllocationViewAllocateActionStateModel>,
    { payload }: AllocationViewAllocateActionLoadRuleSlots,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewAllocateActionService.getAllocationRuleSlots(payload).pipe(
      map((slots: AllocationUliRuleSlot[]) =>
        slots.sort(
          (a: AllocationUliRuleSlot, b: AllocationUliRuleSlot) =>
            a.timeFrame.start.timestamp - b.timeFrame.start.timestamp,
        ),
      ),
      map((slots: AllocationUliRuleSlot[]) => {
        ctx.patchState({
          allocationRuleSlotOptionList: slots,
          loaded: true,
        });
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewAllocateActionLoadRuleSlotsWithoutDetails)
  getAllocationRuleSlotsWithoutDetails(
    ctx: StateContext<AllocationViewAllocateActionStateModel>,
    { payload }: AllocationViewAllocateActionLoadRuleSlotsWithoutDetails,
  ): Observable<void | MatSnackBarRef<SimpleSnackBar>> {
    const emptyDetails = new AllocationDetailsModel({ hubId: payload.hubId });

    return this.allocationViewAllocateActionService
      .getAllocationRuleSlots({ details: emptyDetails, ruleId: payload.ruleId })
      .pipe(
        map((slots: AllocationUliRuleSlot[]) =>
          slots.sort(
            (a: AllocationUliRuleSlot, b: AllocationUliRuleSlot) =>
              a.timeFrame.start.timestamp - b.timeFrame.start.timestamp,
          ),
        ),
        map((slots: AllocationUliRuleSlot[]) => {
          ctx.patchState({
            allocationRuleSlotOptionList: slots,
            loaded: true,
          });
        }),
        catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
      );
  }

  @Action(AllocationViewAllocateActionLoadProposedTimeslot)
  getProposedTimeslot(
    ctx: StateContext<AllocationViewAllocateActionStateModel>,
    { payload }: AllocationViewAllocateActionLoadProposedTimeslot,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewAllocateActionService.getProposedTimeslot(payload).pipe(
      map((proposedTimeslot: AllocationViewTimeslotOutputModel) => {
        ctx.patchState({
          proposedTimeslot: new AllocationViewTimeslotInputModel(proposedTimeslot),
          loaded: true,
        });
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewAllocateActionResetProposedTimeslot)
  resetProposedTimeslot(ctx: StateContext<AllocationViewAllocateActionResetProposedTimeslot>): void {
    ctx.patchState({
      proposedTimeslot: initialState.proposedTimeslot,
    });
  }

  @Action(AllocationViewAllocateActionManualAllocateUli)
  manualAllocateUli(
    ctx: StateContext<AllocationViewAllocateActionStateModel>,
    { payload }: AllocationViewAllocateActionManualAllocateUli,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewAllocateActionService.manualAllocateUli(payload).pipe(
      map((responseData: AllocationResponseModel) => {
        ctx.patchState({ responseData });

        const { closeContainerAfterComplete } = ctx.getState();
        this.finishAction('api.uli.success.allocate', closeContainerAfterComplete);
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewAllocateActionAllocateByRuleUli)
  allocateByRuleUli(
    ctx: StateContext<AllocationViewAllocateActionStateModel>,
    { payload }: AllocationViewAllocateActionAllocateByRuleUli,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewAllocateActionService.allocateByRuleUli(payload).pipe(
      map((responseData: AllocationResponseModel) => {
        ctx.patchState({ responseData });

        const { closeContainerAfterComplete } = ctx.getState();
        this.finishAction('api.uli.success.allocate', closeContainerAfterComplete);
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewAllocateActionAutoAllocateUli)
  autoAllocateUli(
    ctx: StateContext<AllocationViewAllocateActionStateModel>,
    { payload }: AllocationViewAllocateActionAutoAllocateUli,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewAllocateActionService.autoAllocateUli(payload).pipe(
      map((responseData: AllocationResponseModel) => {
        ctx.patchState({ responseData });

        const { closeContainerAfterComplete } = ctx.getState();
        this.finishAction('api.uli.success.allocate', closeContainerAfterComplete);
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewAllocateActionSetCloseAfterCompleteState)
  setCloseContainerAfterCompleteState(
    ctx: StateContext<AllocationViewAllocateActionStateModel>,
    { payload }: AllocationViewAllocateActionSetCloseAfterCompleteState,
  ): void {
    ctx.patchState({ closeContainerAfterComplete: payload });
  }

  @Action(AllocationViewAllocateActionSetInitialState)
  setInitialState(ctx: StateContext<AllocationViewAllocateActionSetInitialState>): void {
    ctx.setState(initialState);
    this.setContainerAfterActionEffectsInitialState();
  }
}
